import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Tag,
  Menu,
  Dropdown,
  message,
  Space,
  Input,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  query,
  where,
  collection,
  getDocs,
  deleteDoc,
  doc,
  addDoc,
  setDoc,
  orderBy,
} from "firebase/firestore";
import * as XLSX from "xlsx";

import { db } from "../firebaseConfig";
import TicketLayout from "./TicketLayout";
import TicketModal from "./TicketModal";
import QRScannerModal from "./QRScannerModal";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const TicketsTable = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isQRReaderModalVisible, setIsQRReaderModalVisible] = useState(false);
  const [isTicketModalVisible, setIsTicketModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingTicket, setEditingTicket] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const ticketRef = useRef();
  //const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const handleDelete = async () => {
    if (selectedTicket) {
      const result = await deleteTicket(selectedTicket.key);
      if (result.success) {
        message.success("Ticket eliminado correctamente.");
        setTickets((prevTickets) =>
          prevTickets.filter((ticket) => ticket.key !== selectedTicket.key),
        );
      } else {
        message.error("Error al eliminar el ticket.");
      }
      setIsDeleteModalVisible(false);
      setSelectedTicket(null);
    }
  };

  const deleteTicket = async (id) => {
    try {
      const ticketRef = doc(db, "tickets", id);
      await deleteDoc(ticketRef);
      return { success: true };
    } catch (error) {
      console.error("Error al eliminar el ticket:", error);
      return { success: false, error };
    }
  };

  const showDeleteModal = (ticket) => {
    setSelectedTicket(ticket);
    setIsDeleteModalVisible(true);
  };

  const handleEditTicket = (ticket) => {
    console.log("editando ticket:", ticket);
    setEditingTicket(ticket); // Configurar el ticket seleccionado
    setIsModalVisible(true); // Mostrar modal
  };

  const handleRefresh = () => {
    setRefreshing(true);
  };

  const exportDataToExcel = async () => {
    try {
      // Obtener todos los documentos de la colección "tickets" desde Firestore
      const querySnapshot = await getDocs(collection(db, "tickets"));

      // Convertir los documentos a un array de objetos
      const tickets = querySnapshot.docs.map((doc) => doc.data());

      if (tickets.length === 0) {
        message.warning("No hay datos para exportar.");
        return;
      }

      // Crear un workbook y un worksheet
      const worksheet = XLSX.utils.json_to_sheet(tickets);
      const workbook = XLSX.utils.book_new();

      // Añadir el worksheet al workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Tickets");

      // Generar el archivo Excel y desencadenar la descarga
      XLSX.writeFile(workbook, "Tickets.xlsx");
      message.success("Exportación completada con éxito.");
    } catch (error) {
      console.error("Error al exportar a Excel:", error);
      message.error("Hubo un error al exportar los datos.");
    }
  };

  const handleViewPDF = (ticket) => {
    setSelectedTicket(ticket); // Configura el ticket seleccionado
    setIsTicketModalVisible(true); // Muestra el modal
  };

  const handleSaveTicket = (updatedTicket) => {
    if (editingTicket) {
      // Actualizar el ticket existente
      console.log("Editando ticket:", updatedTicket);
    } else {
      // Crear un nuevo ticket
      console.log("Creando nuevo ticket:", updatedTicket);
    }
    setEditingTicket(null); // Limpiar estado
  };

  const handleCloseTicketModal = () => {
    setIsTicketModalVisible(false); // Cierra el modal
    setSelectedTicket(null); // Limpia el ticket seleccionado
  };

  const handleDownloadPDF = async () => {
    const element = ticketRef.current;
    const canvas = await html2canvas(element, {
      scale: 2, // Mejora la calidad para pequeñas impresoras
    });
    const data = canvas.toDataURL("image/png");

    const mmToPoints = 2.83465; // Conversión de mm a puntos
    const pdfWidth = 60 * mmToPoints; // 60 mm ancho
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const pdfHeight = (imgHeight * pdfWidth) / imgWidth; // Ajustar la altura proporcionalmente

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: [pdfWidth, pdfHeight], // Tamaño ajustado
    });

    pdf.addImage(data, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`ticket_${selectedTicket.id}.pdf`);
  };

  const handlePrint = async () => {
    const element = ticketRef.current;
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(
        "<html><head><title>Print</title></head><body>",
      );
      printWindow.document.write(element.outerHTML); // Copia el contenido del ticket
      printWindow.document.write("</body></html>");
      printWindow.document.close();
      printWindow.focus();
      printWindow.print(); // Imprime directamente
      printWindow.close(); // Cierra la ventana de impresión
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    //setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    //setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          id="searchInput" // Asigna un id al Input
          placeholder={`Buscar por ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Resetear
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : false,
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => {
          const searchInput = document.getElementById("searchInput");
          if (searchInput) {
            searchInput.select(); // Seleccionar el texto al abrir el filtro
          }
        }, 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <span style={{ backgroundColor: "#ffc069", padding: 0 }}>{text}</span>
      ) : (
        text
      ),
  });

  // Fetch tickets from Firestore
  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      const ticketsCollection = collection(db, "tickets");

      // Consulta Firestore para ordenar por fechaEmision DESC
      const q = query(ticketsCollection, orderBy("fechaEmision", "desc"));

      const ticketSnapshot = await getDocs(q);
      const ticketList = ticketSnapshot.docs.map((doc) => ({
        key: doc.id,
        ...doc.data(),
      }));

      setTickets(ticketList);
      setLoading(false);
    };

    fetchTickets();
  }, [refreshing]); // Se ejecuta cuando cambia 'refreshing'

  // Columns for the table
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 150,
      ...getColumnSearchProps("id"),
      sorter: (a, b) => a.id.localeCompare(b.id),
      render: (id) => {
        return <Tag color="pink">{id.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Nombre",
      dataIndex: "informacionPersonal",
      key: "informacionPersonal",
      ...getColumnSearchProps("informacionPersonal"),
      ellipsis: true,
      sorter: (a, b) =>
        a.informacionPersonal.localeCompare(b.informacionPersonal),
      render: (informacionPersonal) => informacionPersonal,
    },
    {
      title: "Pais",
      dataIndex: "paisOrigen",
      key: "paisOrigen",
      ...getColumnSearchProps("paisOrigen"),
      ellipsis: true,
      sorter: (a, b) => a.paisOrigen.localeCompare(b.paisOrigen),
      render: (paisOrigen) => paisOrigen,
    },
    {
      title: "Provincia",
      dataIndex: "provincia",
      key: "provincia",
      ...getColumnSearchProps("provincia"),
      ellipsis: true,
      sorter: (a, b) => a.provincia.localeCompare(b.provincia),
      render: (provincia) => provincia,
    },
    {
      title: "Fecha de Emisión",
      dataIndex: "fechaEmision",
      key: "fechaEmision",
      width: 200,
      ellipsis: true,
      render: (text) => new Date(text).toLocaleString(), // Mostrar en formato legible
    },
    {
      title: "Personas",
      dataIndex: "grupoSize",
      key: "grupoSize",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Fecha de evento",
      dataIndex: "fechaYHora",
      key: "fechaEmision",
      width: 200,
      ellipsis: true,
      render: (text) => new Date(text).toLocaleString(), // Mostrar en formato legible
    },
    {
      title: "Empresa local",
      dataIndex: "empresaLocal",
      key: "empresaLocal",
      width: 100,
      align: "center",
      ellipsis: true,
      render: (empresaLocal) => {
        let color;
        switch (empresaLocal) {
          case true:
            color = "blue";
            break;
          case false:
            color = "orange";
            break;
          default:
            color = "blue";
        }
        return <Tag color={color}>{empresaLocal ? "SI" : "NO"}</Tag>;
      },
    },
    {
      title: "Guia",
      dataIndex: "guia",
      key: "guia",
      width: 100,
      align: "center",
      ellipsis: true,
      render: (guia) => {
        let color;

        switch (guia) {
          case "municipal":
            color = "yellow";
            break;
          case "propio":
            color = "green";
            break;
          default:
            color = "";
        }
        return <Tag color={color}>{guia.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Tipo de Entrada",
      dataIndex: "tipoEntrada",
      key: "tipoEntrada",
      width: 150,
      align: "center",
      ellipsis: true,
      render: (tipoEntrada) => {
        let color;
        switch (tipoEntrada) {
          case "general":
            color = "blue";
            break;
          case "pensionista":
            color = "green";
            break;
          case "grupo":
            color = "orange";
            break;
          case "centroDocente":
            color = "purple";
            break;
          default:
            color = "gray";
        }
        return <Tag color={color}>{tipoEntrada.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Consumido",
      dataIndex: "consumido",
      key: "consumido",
      width: 150,
      align: "center",
      ellipsis: true,
      render: (consumido) => {
        let color;
        switch (consumido) {
          case true:
            color = "red";
            break;
          case false:
            color = "green";
            break;
          default:
            color = "red";
        }
        return (
          <Tag color={color}>{consumido ? "CONSUMIDO" : "NO CONSUMIDO"}</Tag>
        );
      },
    },
    {
      title: "Precio",
      dataIndex: "precio",
      key: "precio",
      width: 100,
      align: "center",
      ellipsis: true,
      render: (text) => `${text}€`,
    },

    {
      title: "Acciones",
      key: "actions",
      align: "center",
      width: 200,
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item key="edit" onClick={() => handleEditTicket(record)}>
              Editar
            </Menu.Item>
            <Menu.Item key="view-pdf" onClick={() => handleViewPDF(record)}>
              Ver PDF
            </Menu.Item>
            <Menu.Item
              key="delete-ticket"
              onClick={() => showDeleteModal(record)}
            >
              Eliminar
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button type="link" style={{ fontWeight: "900", color: "#5e2257" }}>
              Opciones
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  // Generate PDF for a ticket

  // Open and close modal

  const handleCancel = () => {
    form.resetFields();
    setIsModalVisible(false);
  };

  const generateUniqueId = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"; // Letras y números
    let id = "";
    for (let i = 0; i < 7; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  };

  const handleCreateTicket = async (values) => {
    try {
      console.log("Valores del formulario:", values);

      const fechaEmision = new Date().toISOString(); // Fecha y hora actual

      // Verificar si se está editando un ticket o creando uno nuevo
      if (values.id) {
        // Si está editando, buscar el ticket por su ID
        const ticketQuery = query(
          collection(db, "tickets"),
          where("id", "==", values.id), // Buscar por la propiedad id
        );

        const querySnapshot = await getDocs(ticketQuery);

        if (!querySnapshot.empty) {
          const ticketDoc = querySnapshot.docs[0]; // Obtener el primer resultado
          const ticketRef = doc(db, "tickets", ticketDoc.id); // Referencia al documento

          // Actualizar los campos del ticket
          const updatedTicket = {
            ...ticketDoc.data(),
            ...values,
            fechaYHora: values.fechaYHora?.toISOString() || null, // Convertir fecha a formato ISO
            empresaLocal: values.empresaLocal,
          };

          await setDoc(ticketRef, updatedTicket); // Actualizar
          message.success(
            `Ticket actualizado correctamente (ID: ${values.id})`,
          );

          setTickets((prevTickets) =>
            prevTickets.map((ticket) =>
              ticket.id === values.id
                ? { key: values.id, ...updatedTicket }
                : ticket,
            ),
          );
        } else {
          message.error("El ticket no existe");
        }
      } else {
        // Si está creando un nuevo ticket
        const ticketId = generateUniqueId(); // Generar un ID único

        const newTicket = {
          id: ticketId,
          fechaEmision,
          precio: values.precio,
          email: values.email,
          informacionPersonal: values.informacionPersonal,
          enviarEmail: values.enviarEmail,
          nombreTecnico: "Técnico",
          guia: values.guia,
          grupoSize: values.grupoSize,
          tipoEntrada: values.tipoEntrada,
          visita: values.visita,
          paisOrigen: values.paisOrigen,
          fechaYHora: values.fechaYHora?.toISOString() || null,
          empresaLocal: values.empresaLocal === true,
          consumido: false,
        };

        //console.log("Valores del formulario:", newTicket);

        // Guardar en Firestore
        await addDoc(collection(db, "tickets"), newTicket);
        message.success(
          `Ticket creado correctamente con ID: ${ticketId} y precio: ${newTicket.precio}€`,
        );
        setTickets((prevTickets) => [
          ...prevTickets,
          { key: ticketId, ...newTicket },
        ]);
      }

      form.resetFields();
      setIsModalVisible(false);
    } catch (error) {
      console.error("Error al guardar el ticket:", error);
      message.error("Hubo un problema al guardar el ticket");
    }
  };

  return (
    <div>
      <div style={{ marginBottom: "20px" }}>
        <Space>
          <Button type="primary" size="large" onClick={exportDataToExcel}>
            Exportar a Excel
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={tickets}
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <TicketModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSave={handleSaveTicket}
        ticketData={editingTicket}
        handleCreateTicket={handleCreateTicket}
        handleCancel={handleCancel}
      />
      <QRScannerModal
        visible={isQRReaderModalVisible}
        setIsQRReaderModalVisible={setIsQRReaderModalVisible}
        handleRefresh={handleRefresh}
      />

      <Modal
        title={`Ticket ${selectedTicket?.id}`}
        visible={isTicketModalVisible}
        onCancel={handleCloseTicketModal}
        footer={null}
        width={400}
      >
        {selectedTicket && (
          <TicketLayout ref={ticketRef} ticketData={selectedTicket} />
        )}

        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <Button type="primary" size="large" onClick={handleDownloadPDF}>
            Descargar PDF
          </Button>
          <Button
            type="default"
            size="large"
            style={{ marginLeft: "10px" }}
            onClick={handlePrint}
          >
            Imprimir
          </Button>
          <Button
            size="large"
            style={{ marginLeft: "10px" }}
            onClick={handleCloseTicketModal}
          >
            Cerrar
          </Button>
        </div>
      </Modal>

      <Modal
        title="Confirmar eliminación"
        visible={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="Eliminar"
        cancelText="Cancelar"
      >
        <p>¿Estás seguro de que deseas eliminar el ticket seleccionado?</p>
      </Modal>
    </div>
  );
};

export default TicketsTable;
